import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

export function useApiKeys(customerId) {
	const [keys, setKeys] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	const fetchKeys = useCallback(async () => {
		if (!customerId) return
		try {
			const { data, error } = await supabase
				.from('customer_api_keys')
				.select('id, name, key_prefix, scopes, expires_at, last_used_at, revoked_at, created_at')
				.eq('customer_id', customerId)
				.order('created_at', { ascending: false })

			if (error) throw error
			setKeys(data || [])
		} catch (err) {
			console.error('Error fetching API keys:', err)
			setError(err.message)
		}
	}, [customerId])

	const createKey = useCallback(async (name, expiresAt) => {
		if (!customerId) throw new Error('No customer ID')

		// Generate random key client-side
		const randomBytes = new Uint8Array(32)
		crypto.getRandomValues(randomBytes)
		const rawKey = btoa(String.fromCharCode(...randomBytes))
			.replace(/\+/g, '-')
			.replace(/\//g, '_')
			.replace(/=+$/, '')
		const fullKey = `ak_${rawKey}`
		const prefix = fullKey.substring(0, 8)

		// Hash the key with SHA-256
		const keyBytes = new TextEncoder().encode(fullKey)
		const hashBuffer = await crypto.subtle.digest('SHA-256', keyBytes)
		const hashArray = Array.from(new Uint8Array(hashBuffer))
		const keyHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

		// Store hash in database
		const { error } = await supabase
			.from('customer_api_keys')
			.insert({
				customer_id: customerId,
				name,
				key_hash: keyHash,
				key_prefix: prefix,
				expires_at: expiresAt || null,
			})
			.select()
			.single()

		if (error) throw error

		await fetchKeys()

		// Return the full key - this is the only time it's available
		return fullKey
	}, [customerId, fetchKeys])

	const revokeKey = useCallback(async (keyId) => {
		const { error } = await supabase
			.from('customer_api_keys')
			.update({ revoked_at: new Date().toISOString() })
			.eq('id', keyId)

		if (error) throw error
		await fetchKeys()
	}, [fetchKeys])

	useEffect(() => {
		const load = async () => {
			setLoading(true)
			await fetchKeys()
			setLoading(false)
		}
		load()
	}, [fetchKeys])

	return { keys, loading, error, createKey, revokeKey, fetchKeys }
}
